import { Injectable } from '@angular/core';
import { User } from '../resources/user/user.interface';
import { UserService } from '../resources/user/user.service';
import { AUTH_PERMISSIONS } from './auth.constants';
import { ProjectService } from '../resources/project/project.service';
import { AuthPipelineConfig } from './auth.interface';
import { Project } from './../resources/project/project.interface';
import { Resource } from '../resources/resource.interface';

/**
 * Authentication service.
 *
 * This service contains logic for the authentication and the current active user.
 *
 * @todo AuthGuards for protecting routes.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  /**
   * Current active user.
   *
   * @todo Use UserService instead of a local copy, but there seems to be a problem with service load
   *  order because of the AngularJS ui-router. Can probably be fixed after migration to Angular Router.
   */
  private currentUser: User;

  /** Local copy of current project. */
  private project: Project;

  /** Set default config values. */
  private readonly CONFIG_DEFAULT: AuthPipelineConfig = { ignore: [] };

  /** Constructor. */
  constructor(
    private userService: UserService,
    private projectService: ProjectService
  ) {
    this.currentUser = this.userService.getUserCurrent();
    this.projectService.project$.subscribe(project => {
      this.project = project;
    });
  }

  /**
   * Check if the current user can create the entity.
   *
   * @param entity The entity to check the rights for.
   * @returns Whether the user can create the current entity.
   */
  public canCreate(entity: Resource['entity'], config?: AuthPipelineConfig): boolean {
    config = { ...this.CONFIG_DEFAULT, ...config, entity: entity };
    switch (entity) {
      case 'area':
        return this._hasPermission({ ...config, permission: 'project:building:area:create' });
      case 'basicarea':
        return this._hasPermission({ ...config, permission: 'project:basicarea:create' });
      case 'building':
        return this._hasPermission({ ...config, permission: 'project:building:create' });
      case 'comment':
        return this._hasPermission({ ...config, permission: 'project:element:comment:create' });
      case 'cluster':
        return this._hasPermission({ ...config, permission: 'project:building:cluster:create' });
      case 'element':
        return this._hasPermission({ ...config, permission: 'project:element:create' });
      case 'module':
        return this._hasPermission({ ...config, permission: 'project:module:create' });
      case 'pdf':
        return this._hasPermission({ ...config, permission: 'project:pdf' });
      case 'excel':
        return this._hasPermission({ ...config, permission: 'project:excel' });
      case 'project':
        return this._hasPermission({ ...config, permission: 'project:create' });
      case 'projects':
        return this._hasPermission({ ...config, permission: 'projects:create' });
      case 'submodule':
        return this._hasPermission({ ...config, permission: 'project:submodule:create' });
      default:
        return false;
    }
  }

  /**
   * Check if the current user can delete the entity.
   *
   * Note: Some entities need to be *updated* instead of deleted, by setting `{ active: false }`.
   *
   * @param entity The entity to check the rights for.
   * @returns Whether the user can delete the current entity.
   */
  public canDelete(entity: Resource['entity'], config?: AuthPipelineConfig): boolean {
    config = { ...this.CONFIG_DEFAULT, ...config, entity: entity };
    switch (entity) {
      case 'area':
        return this._hasPermission({ ...config, permission: 'project:building:area:update' });
      case 'building':
        return this._hasPermission({ ...config, permission: 'project:building:delete' });
      case 'cluster':
        return this._hasPermission({ ...config, permission: 'project:building:cluster:update' });
      case 'element':
        return this._hasPermission({ ...config, permission: 'project:element:update' });
      case 'module':
        return this._hasPermission({ ...config, permission: 'project:module:delete' });
      case 'project':
        return this._hasPermission({ ...config, permission: 'project:delete' });
      case 'submodule':
        return this._hasPermission({ ...config, permission: 'project:submodule:delete' });
      default:
        return false;
    }
  }

  /**
   * Check if the current user can read (view) the entity.
   *
   * @param entity The entity to check the rights for.
   * @returns Whether the user can read the current entity.
   */
  public canRead(entity: Resource['entity'], config?: AuthPipelineConfig): boolean {
    config = { ...this.CONFIG_DEFAULT, ...config, entity: entity };
    switch (entity) {
      case 'area':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'basicarea':
        return this._hasPermission({ ...config, permission: 'project:basicarea:read' });
      case 'building':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'cluster':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'element':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'log':
        return this._hasPermission({ ...config, permission: 'project:element:log:read' });
      case 'module':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'project':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'projects':
        return this._hasPermission({ ...config, permission: 'projects:read' });
      case 'submodule':
        return this._hasPermission({ ...config, permission: 'project:read' });
      case 'element-logs':
        return this._hasPermission({ ...config, permission: 'project:element:logs:read' });
      case 'element-contextmenu':
        return this._hasPermission({ ...config, permission: 'project:element:contextmenu:read' });
      case 'element-assignment':
        return this._hasPermission({ ...config, permission: 'project:element:assignment:read' });
      case 'element-comment':
        return this._hasPermission({ ...config, permission: 'project:element:comment:read' });
      case 'overview-project':
        return this._hasPermission({ ...config, permission: 'project:overview-project:read' }, false);
      case 'overview-progress':
        return this._hasPermission({ ...config, permission: 'project:overview-progress:read' }, false);
      case 'overview-team':
        return this._hasPermission({ ...config, permission: 'project:overview-team:read' }, false);
      case 'overview-comments':
        return this._hasPermission({ ...config, permission: 'project:overview-comments:read' });
      case 'overview-activities':
        return this._hasPermission({ ...config, permission: 'project:overview-activities:read' });
      case 'overview-assigned':
        return this._hasPermission({ ...config, permission: 'project:overview-assigned:read' });
      case 'overview-download':
        return this._hasPermission({ ...config, permission: 'project:overview-download:read' }, false);
      case 'overview-arealist':
        return this._hasPermission({ ...config, permission: 'project:overview-arealist:read' }, false);
      default:
        return false;
    }
  }

  /**
   * Check if the current user can update the entity.
   *
   * @param entity The entity to check the rights for.
   * @returns Whether the user can update the current entity.
   */
  public canUpdate(entity: Resource['entity'], config?: AuthPipelineConfig): boolean {
    config = { ...this.CONFIG_DEFAULT, ...config, entity: entity };
    switch (entity) {
      case 'area':
        return this._hasPermission({ ...config, permission: 'project:building:area:update' });
      case 'basicarea':
        return this._hasPermission({ ...config, permission: 'project:basicarea:update' });
      case 'building':
        return this._hasPermission({ ...config, permission: 'project:building:update' });
      case 'cluster':
        return this._hasPermission({ ...config, permission: 'project:building:cluster:update' });
      case 'element':
        return this._hasPermission({ ...config, permission: 'project:element:update' });
      case 'module':
        return this._hasPermission({ ...config, permission: 'project:module:update' });
      case 'project':
        return this._hasPermission({ ...config, permission: 'project:update' });
      case 'submodule':
        return this._hasPermission({ ...config, permission: 'project:submodule:update' });
      case 'overview-team':
        return this._hasPermission({ ...config, permission: 'project:overview-team:update' });
      case 'overview-progress':
        return this._hasPermission({ ...config, permission: 'project:overview-progress:update' });
      default:
        return false;
    }
  }

  /**
   * Check if the current user is an admin.
   *
   * @returns Whether the current user is an admin.
   */
  public isAdmin(): boolean {
    return this.currentUser.role === 'admin';
  }

  /**
   * Check if the current user has permission for the curent operation.
   *
   * This method acts as a pipeline, to which each check can be added. Every result from each
   * pipeline method is added as a boolean to the array. Only when *all* the values are `true`,
   * the pipeline will approve the permission.
   *
   * @todo Currently authentication only works *within* a project. For anything that's not in a
   *  project's scope, such as templates, authentication should be ignored (for now). This does not
   *  actually give rights to guest users, but it prevents errors with Angular components within
   *  AngularJS templates.
   *
   * @param config Pipeline configuration.
   * @param permissionByProjectState=true checks for the project state or not
   * @returns Whether the current operation may be executed.
   */
  private _hasPermission(config: AuthPipelineConfig, permissionByProjectState: boolean = true): boolean {
    if (!this.project) {
      return false;
    }

    const pipeline = [];
    pipeline.push(!config.ignore.includes('permission') ? this._hasPermissionByRole(config.permission) : true);
    if (permissionByProjectState) {
      pipeline.push(!config.ignore.includes('projectState') ? this._hasPermissionByProjectState() : true);
    } else {
      pipeline.push(true);
    }

    return !pipeline.includes(false);
  }

  /**
   * Check if the current user has permission based on role/permissions.
   *
   * @todo If the current user is not (yet) set, check again on runtime. When migrated to Angular Router,
   *  a canActivate guard should be added to the base route to make sure the application only loads when
   *  all the users are loaded *and available in each component/service*. For AngularJS controllers this
   *  is resolved by adding a timeout and loading this service through the $injector (kind of hacky), but
   *  this does not work in Angular.
   * @todo When switching user *without* closing the application, the current active role is not refreshed.
   *
   * @param permission Database permission string.
   * @returns Whether the current active user has sufficient rights.
   */
  private _hasPermissionByRole(permission: string): boolean {

    // If the current user is not (yet) set, check again on runtime.
    if (!this.currentUser) {
      this.currentUser = this.userService.getUserCurrent();
    }

    const appRole = `app:${ this.currentUser.role }`;
    const projectRole = `project:${ this.currentUser.project.role }`;

    // Testing roles
    // const appRole = 'app:guest';
    // const projectRole = 'project:guest';

    return [
      AUTH_PERMISSIONS[appRole] && AUTH_PERMISSIONS[appRole].indexOf(permission) > -1,
      AUTH_PERMISSIONS[projectRole] && AUTH_PERMISSIONS[projectRole].indexOf(permission) > -1
    ].indexOf(true) > -1;
  }

  /**
   * Check if the current project is archived.
   *
   * @returns Whether the current project is archived.
   */
  private _hasPermissionByProjectState(): boolean {
    return !this.project.archived;
  }
}
